import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { SnakeState, GAME_CONSTANTS } from '@/types/game';

interface SnakeProps {
  snake: SnakeState;
  isPlayer?: boolean;
}

export const Snake = ({ snake, isPlayer = false }: SnakeProps) => {
  const groupRef = useRef<THREE.Group>(null);
  const segmentRefs = useRef<THREE.Mesh[]>([]);
  const timeRef = useRef(0);

  const snakeColor = useMemo(() => new THREE.Color(snake.color), [snake.color]);
  const glowColor = useMemo(() => new THREE.Color(snake.color).multiplyScalar(0.5), [snake.color]);

  // Calculate segment radius based on score - grows more noticeably with mass
  const baseRadius = GAME_CONSTANTS.SNAKE_BASE_RADIUS;
  // Logarithmic growth: grows quickly at first, then slows down
  // At score 10: ~1.30x, score 50: ~1.78x, score 100: ~1.92x, score 500: ~2.24x
  const growthFactor = 1 + Math.log10(1 + snake.score * 0.1) * 0.4;
  const headRadius = baseRadius * growthFactor;

  useFrame((state, delta) => {
    timeRef.current += delta * 3;
    
    // Apply swimming animation to segments
    segmentRefs.current.forEach((mesh, index) => {
      if (mesh && snake.segments[index]) {
        const segment = snake.segments[index];
        
        // Sinusoidal wave for swimming effect
        const waveOffset = Math.sin(timeRef.current + index * GAME_CONSTANTS.WAVE_FREQUENCY) 
          * headRadius * GAME_CONSTANTS.WAVE_AMPLITUDE;
        
        // Get perpendicular direction for wave
        const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(snake.quaternion);
        const right = new THREE.Vector3(1, 0, 0).applyQuaternion(snake.quaternion);
        
        mesh.position.copy(segment);
        mesh.position.add(right.multiplyScalar(waveOffset));
        
        // Taper segments toward tail
        const taper = 1 - (index / (snake.segments.length + 1)) * 0.5;
        mesh.scale.setScalar(taper);
      }
    });
  });

  // Invincibility flashing effect
  const opacity = snake.invincible 
    ? 0.7 + Math.sin(Date.now() * 0.01) * 0.2 
    : 1;

  return (
    <group ref={groupRef}>
      {/* Head */}
      <mesh position={snake.position} quaternion={snake.quaternion}>
        <sphereGeometry args={[headRadius, 16, 16]} />
        <meshStandardMaterial
          color={snakeColor}
          emissive={snakeColor}
          emissiveIntensity={0.4}
          roughness={0.3}
          metalness={0.2}
          transparent={snake.invincible}
          opacity={opacity}
        />
      </mesh>

      {/* Eyes (only for detailed view) */}
      {isPlayer && (
        <>
          <mesh 
            position={[
              snake.position.x + headRadius * 0.4,
              snake.position.y + headRadius * 0.3,
              snake.position.z + headRadius * 0.7
            ]}
          >
            <sphereGeometry args={[headRadius * 0.25, 8, 8]} />
            <meshStandardMaterial color="white" emissive="white" emissiveIntensity={0.5} />
          </mesh>
          <mesh 
            position={[
              snake.position.x - headRadius * 0.4,
              snake.position.y + headRadius * 0.3,
              snake.position.z + headRadius * 0.7
            ]}
          >
            <sphereGeometry args={[headRadius * 0.25, 8, 8]} />
            <meshStandardMaterial color="white" emissive="white" emissiveIntensity={0.5} />
          </mesh>
        </>
      )}

      {/* Body segments */}
      {snake.segments.map((segment, index) => {
        const segmentRadius = headRadius * (1 - (index / (snake.segments.length + 1)) * 0.4);
        return (
          <mesh
            key={index}
            ref={(el) => { if (el) segmentRefs.current[index] = el; }}
            position={segment}
          >
            <sphereGeometry args={[segmentRadius, 12, 12]} />
            <meshStandardMaterial
              color={snakeColor}
              emissive={snakeColor}
              emissiveIntensity={0.3}
              roughness={0.3}
              metalness={0.2}
              transparent={snake.invincible}
              opacity={opacity}
            />
          </mesh>
        );
      })}

      {/* Name label (for other snakes) */}
      {!isPlayer && (
        <sprite position={[snake.position.x, snake.position.y + headRadius * 3, snake.position.z]}>
          {/* Name would go here - using billboard text */}
        </sprite>
      )}
    </group>
  );
};
